import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  CardMedia,
  CircularProgress,
  MobileStepper
} from '@mui/material';
import {
  SportsBasketball,
  FitnessCenter as FitnessCenterIcon,
  Psychology as PsychologyIcon,
  EmojiEvents as EmojiEventsIcon,
  Close as CloseIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from '@mui/icons-material';
import axiosInstance from '../utils/axiosConfig';
import SEO from '../components/SEO';

const features = [
  {
    icon: <SportsBasketball sx={{ fontSize: 40, color: '#755985' }} />,
    title: 'Skill Development',
    description: 'Comprehensive training focusing on fundamentals and advanced techniques.',
  },
  {
    icon: <FitnessCenterIcon sx={{ fontSize: 40, color: '#755985' }} />,
    title: 'Performance Training',
    description: 'Enhance your athletic performance with specialized conditioning programs.',
  },
  {
    icon: <PsychologyIcon sx={{ fontSize: 40, color: '#755985' }} />,
    title: 'Character Building',
    description: 'Develop leadership, discipline, and mental toughness both on and off the court.',
  },
  {
    icon: <EmojiEventsIcon sx={{ fontSize: 40, color: '#755985' }} />,
    title: 'Elite Training',
    description: 'Advanced programs designed for serious athletes aiming to compete at higher levels.',
  },
];

const Home = () => {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [mainImage, setMainImage] = useState(null);  
  const [testimonials, setTestimonials] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [trainingPaths, setTrainingPaths] = useState([]);
  const [pageContent, setPageContent] = useState({
    title: '',
    subtitle: ''
  });

  const fetchPageContent = async () => {
    try {
      const response = await axiosInstance.get('/api/v2/settings/main-page');
      setPageContent(response.data);
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  const fetchMainImage = async () => {
    try {
      const response = await axiosInstance.get('/api/v2/settings/main-image');
      if (response.data?.url) {
        setMainImage(response.data.url);
      }
    } catch (error) {
      console.error('Error fetching main image:', error);
    }
  };

  const fetchTrainers = async () => {
    try {
      const response = await axiosInstance.get('/api/v2/trainers');
      if (response.data && Array.isArray(response.data)) {
        const sortedTrainers = response.data.sort((a, b) => {
          if (a.featured) return -1;
          if (b.featured) return 1;
          return a.order - b.order;
        });
        setTrainers(sortedTrainers);
      }
    } catch (error) {
      console.error('Error fetching trainers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axiosInstance.get('/api/v2/testimonials');
      console.log('Raw testimonials response:', response.data);
      if (response.data && Array.isArray(response.data)) {
        // Only set active testimonials
        const activeTestimonials = response.data.filter(t => t.isActive);
        console.log('Active testimonials:', activeTestimonials);
        setTestimonials(activeTestimonials);
      }
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const fetchTrainingPaths = async () => {
    try {
      const response = await axiosInstance.get('/api/v2/training-paths');
      if (response.data && Array.isArray(response.data)) {
        setTrainingPaths(response.data);
      }
    } catch (error) {
      console.error('Error fetching training paths:', error);
    }
  };

  useEffect(() => {
    fetchPageContent();
    fetchMainImage();
    fetchTrainers();
    fetchTestimonials();
    fetchTrainingPaths();
  }, []);

  const handleOpenDialog = (trainer) => {
    setSelectedTrainer(trainer);
  };

  const handleCloseDialog = () => {
    setSelectedTrainer(null);
  };

  // Handle testimonial navigation
  const handleNext = () => {
    console.log('Next clicked, current step:', activeStep);
    if (activeStep < Math.ceil(testimonials.length / 3) - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    console.log('Back clicked, current step:', activeStep);
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  useEffect(() => {
    console.log('Current active step:', activeStep);
    console.log('Current visible testimonials:', testimonials);
  }, [activeStep, testimonials]);

  return (
    <Box 
      component="main"
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: '100vh',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        position: 'relative',
        bgcolor: '#f5f5f5'
      }}
    >
      <SEO />
      {/* Hero Section */}
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          height: { xs: '550px', md: '650px' },
          margin: 0,
          padding: 0,
          bgcolor: '#000',
          backgroundImage: 'url(/main_image3.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(180deg, rgba(117,89,133,0.4) 0%, rgba(117,89,133,0.8) 100%)',
            zIndex: 1,
          }
        }}
      >
        {/* Content Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: '2rem', md: '4rem' },
            left: 0,
            right: 0,
            zIndex: 2,
            px: { xs: 2, md: 0 }
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 800, color: 'white' }}>
              <Typography
                variant="h1"
                sx={{ 
                  mb: { xs: 2, md: 3 }, 
                  fontSize: { xs: '2rem', md: '4rem' }, 
                  fontWeight: 600,
                  lineHeight: 1.2
                }}
              >
                {pageContent.title}
              </Typography>
              <Typography
                variant="h2"
                sx={{ 
                  mb: { xs: 3, md: 4 }, 
                  fontSize: { xs: '0.6rem !important', md: '1.2rem !important' }, 
                  fontWeight: 400,
                  opacity: 0.9,
                  lineHeight: 1.5
                }}
              >
                {pageContent.subtitle}
              </Typography>
              <Button
                variant="contained"
                size="large"
                component={RouterLink}
                to="/schedule"
                sx={{
                  py: { xs: 1.25, md: 1.5 },
                  px: { xs: 3, md: 4 },
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  backgroundColor: '#C0C0C0',
                  '&:hover': {
                    backgroundColor: '#A8A8A8',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(192,192,192,0.4)',
                  },
                }}
              >
                Begin Your Journey
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 4, bgcolor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: '#000000',
                letterSpacing: '0.02em',
                mb: 1
              }}
            >
              UNLOCK YOUR POTENTIAL
            </Typography>
          </Box>

          <Grid container spacing={2} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 0 20px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 40px rgba(117,89,133,0.2)',
                      '& .icon': {
                        transform: 'scale(1.1)',
                      }
                    }
                  }}
                >
                  <Box 
                    className="icon"
                    sx={{ 
                      mb: 2,
                      transition: 'transform 0.3s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: 'rgba(117,89,133,0.05)',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      mb: 2,
                      color: '#755985',
                      textAlign: 'center'
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    align="center"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Meet Your Trainer Section */}
      <Box sx={{ py: 4, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: '#000000',
                mb: 1
              }}
            >
              Meet Your Trainers
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 'sm',
                mx: 'auto',
                mb: 1
              }}
            >
              Work with experienced trainers who are dedicated to your success
            </Typography>
          </Box>

          {loading && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && trainers.length > 0 && (
            <Grid 
              container 
              spacing={4} 
              justifyContent="center"
              sx={{ 
                width: '100%',
                maxWidth: '1200px',
                mx: 'auto',
                px: { xs: 2, md: 0 }
              }}
            >
              {trainers.map((trainer, index) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={trainers.length === 3 ? 4 : 3}
                  key={trainer.id}
                >
                  <Card 
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                      maxWidth: trainers.length === 3 ? '380px' : '340px',
                      mx: 'auto',
                      bgcolor: 'white',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 30px rgba(117,89,133,0.1)',
                      }
                    }}
                  >
                    <Box sx={{ 
                      position: 'relative',
                      width: '100%',
                      height: trainers.length === 3 ? '380px' : '340px',
                      bgcolor: '#fafafa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}>
                      <CardMedia
                        component="img"
                        sx={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.03)'
                          }
                        }}
                        image={trainer.imageUrl || '/images/trainer-placeholder.jpg'}
                        alt={trainer.name}
                      />
                    </Box>
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      p: 2.5
                    }}>
                      <Typography 
                        variant="h5" 
                        sx={{
                          fontWeight: 600,
                          fontSize: '1.25rem',
                          mb: 0.5,
                          color: '#755985'
                        }}
                      >
                        {trainer.name}
                      </Typography>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          mb: 1.5,
                          color: '#a486b4',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        {trainer.title}
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          mb: 2.5,
                          fontSize: '0.875rem',
                          lineHeight: 1.6,
                          height: '2.8em',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {trainer.description}
                      </Typography>
                      <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Button 
                          variant="text" 
                          onClick={() => handleOpenDialog(trainer)}
                          sx={{ 
                            py: 0.75,
                            fontSize: '0.875rem',
                            textTransform: 'none',
                            color: '#755985',
                            '&:hover': {
                              backgroundColor: 'rgba(117,89,133,0.04)'
                            }
                          }}
                        >
                          Learn More
                        </Button>
                        <Button
                          variant="contained"
                          component={RouterLink}
                          to="/schedule"
                          sx={{
                            py: 1,
                            fontSize: '0.875rem',
                            textTransform: 'none',
                            borderRadius: '8px',
                            backgroundColor: '#755985',
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: '#755985',
                              boxShadow: '0 4px 12px rgba(117,89,133,0.2)'
                            }
                          }}
                        >
                          Train Now
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Training Paths Section */}
      <Box sx={{ 
        bgcolor: '#f5f5f5', 
        py: 4,
        px: 2
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: '#000000',
                mb: 1
              }}
            >
              Choose Your Path
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 'sm',
                mx: 'auto',
                mb: 1
              }}
            >
              Select the training program that best fits your goals and skill level
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {trainingPaths.map((path) => (
              <Grid item xs={12} md={4} key={path.title}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 4,
                    borderRadius: 2,
                    transition: 'all 0.3s ease-in-out',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 40px -12px rgba(117,89,133,0.2)',
                      '& .MuiButton-root': {
                        bgcolor: '#755985',
                        color: 'white',
                        borderColor: '#755985'
                      }
                    }
                  }}
                >
                  <Typography 
                    variant="h4" 
                    align="center" 
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: path.popular ? '#755985' : 'inherit'
                    }}
                  >
                    {path.title}
                  </Typography>
                  <Typography
                    variant="h3"
                    align="center"
                    sx={{ 
                      mb: 3,
                      color: '#755985',
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'center'
                    }}
                  >
                    {path.priceDisplay}
                  </Typography>
                  <Box 
                    sx={{ 
                      mb: 3, 
                      flexGrow: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      pt: 3
                    }}
                  >
                    {path.features.map((feature, i) => (
                      <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        alignItems: 'start',
                        gap: 1,
                        mb: 2
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontSize: '1.2rem', color: '#755985', mt: '3px' }}
                      >
                        •
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary'
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                    ))}
                  </Box>
                  <Button
                    variant="outlined"
                    fullWidth
                    component={RouterLink}
                    to="/contact"
                    sx={{ 
                      mt: 'auto',
                      py: 1.5,
                      borderWidth: 2,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2
                      }
                    }}
                  >
                    Begin Your Journey
                  </Button>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: 8, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#000000',
                mb: 1
              }}
            >
              What Our Athletes Say
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 'sm',
                mx: 'auto',
                mb: 4
              }}
            >
              Hear from athletes who have experienced the Narrow Gate difference
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} 
                sx={{ 
                  display: Math.floor(index / 3) === activeStep ? 'block' : 'none'
                }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 4,
                    borderRadius: 2,
                    transition: 'all 0.3s ease-in-out',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 40px -12px rgba(117,89,133,0.2)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                          mb: 0.5,
                          fontWeight: 600
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography 
                        variant="subtitle2" 
                        color="primary"
                        sx={{ fontWeight: 500 }}
                      >
                        {testimonial.level}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      fontStyle: 'italic',
                      lineHeight: 1.8,
                      flexGrow: 1,
                      position: 'relative'
                    }}
                  >
                    {testimonial.testimonial}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box 
            sx={{ 
              mt: 4,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              position: 'relative',
              zIndex: 10
            }}
          >
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{
                minWidth: 'auto',
                p: 1,
                color: '#755985',
                '&.Mui-disabled': {
                  color: 'rgba(117,89,133,0.3)'
                },
                '&:hover': {
                  backgroundColor: 'rgba(117,89,133,0.1)'
                }
              }}
            >
              <KeyboardArrowLeft />
            </Button>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {[...Array(Math.ceil(testimonials.length / 3))].map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setActiveStep(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: index === activeStep ? '#755985' : 'rgba(117,89,133,0.3)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                />
              ))}
            </Box>

            <Button
              onClick={handleNext}
              disabled={activeStep === Math.ceil(testimonials.length / 3) - 1}
              sx={{
                minWidth: 'auto',
                p: 1,
                color: '#755985',
                '&.Mui-disabled': {
                  color: 'rgba(117,89,133,0.3)'
                },
                '&:hover': {
                  backgroundColor: 'rgba(117,89,133,0.1)'
                }
              }}
            >
              <KeyboardArrowRight />
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: '#755985',
          color: 'white',
          py: 4,
          textAlign: 'center',
          position: 'relative'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'white'
            }}
          >
            Ready to Transform Your Game?
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: 'white',
              maxWidth: 'sm',
              mx: 'auto',
              mb: 3
            }}
          >
            Join Narrow Gate Training today and take your skills to the next level
          </Typography>
          <Button
            component={RouterLink}
            to="/contact"
            variant="contained"
            sx={{
              py: 2,
              px: 4,
              fontSize: '1.1rem',
              backgroundColor: '#C0C0C0',
              '&:hover': {
                backgroundColor: '#A8A8A8',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(192,192,192,0.4)',
              },
            }}
          >
            Contact Us Now
          </Button>
        </Container>
      </Box>

      {/* Trainer Dialog */}
      <Dialog
        open={Boolean(selectedTrainer)}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        {selectedTrainer && (
          <>
            <DialogTitle>
              {selectedTrainer.name}
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {selectedTrainer.imageUrl && (
                <Box sx={{ mb: 2 }}>
                  <img
                    src={selectedTrainer.imageUrl}
                    alt={selectedTrainer.name}
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px'
                    }}
                  />
                </Box>
              )}
              <Typography variant="body1">
                {selectedTrainer.description}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Home;
